<template>
  <div class="user-management">
    <el-tabs v-model="activeTab" class="admin-tabs">
      <el-tab-pane label="用户账号" name="accounts">
        <el-card>
          <template #header>
            <div class="card-header">
              <div style="display: flex; align-items: center; gap: 20px;">
                <span style="font-size: 16px; font-weight: bold;">用户管理</span>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 14px; color: #606266;">VIP功能限制：</span>
                  <el-switch
                    v-model="vipEnabled"
                    active-text="开启"
                    inactive-text="关闭（全员免费）"
                    @change="handleVipToggle"
                    :loading="vipToggleLoading"
                  />
                </div>
              </div>
              <el-button type="primary" @click="openCreateDialog">新增用户</el-button>
            </div>
          </template>

          <el-table v-loading="loading" :data="users" stripe>
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="username" label="用户名" min-width="140" />
            <el-table-column prop="school" label="所属学校" min-width="160" />
            <el-table-column label="角色" width="100">
              <template #default="{ row }">
                <el-tag :type="row.is_admin ? 'danger' : 'info'">
                  {{ row.is_admin ? '管理员' : '普通用户' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="row.is_active ? 'success' : 'warning'">
                  {{ row.is_active ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="VIP状态" width="100">
              <template #default="{ row }">
                <el-tag :type="isRowVip(row) ? 'success' : 'info'">
                  {{ isRowVip(row) ? 'VIP' : '普通' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="VIP到期时间" min-width="180">
              <template #default="{ row }">
                <span>{{ row.is_vip ? (formatVipExpiredAt(row.vip_expired_at) || '永久') : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" min-width="180">
              <template #default="{ row }">
                <span>{{ formatDateTime(row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="最后在线时间" min-width="180">
              <template #default="{ row }">
                <el-tag v-if="!row.last_active_at" type="info" size="small">从未在线</el-tag>
                <span v-else>{{ formatDateTime(row.last_active_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" fixed="right">
              <template #default="{ row }">
                <el-button type="primary" size="small" link @click="openEditDialog(row)">编辑</el-button>
                <el-button
                  v-if="row.id !== currentUser?.id"
                  type="warning"
                  size="small"
                  link
                  @click="handleSuperLogin(row)"
                >
                  超级登录
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="用户反馈" name="feedback">
        <el-card>
          <template #header>
            <div class="card-header feedback-header">
              <span style="font-size: 16px; font-weight: bold;">产品意见与反馈</span>
              <div class="feedback-filters">
                <el-select v-model="filterType" placeholder="反馈类型" clearable @change="loadFeedbacks" style="width: 140px; margin-right: 8px">
                  <el-option label="🐛 功能问题" value="bug" />
                  <el-option label="✨ 需求想法" value="feature" />
                  <el-option label="💡 意见建议" value="suggestion" />
                  <el-option label="❓ 其它" value="other" />
                </el-select>
                <el-select v-model="filterStatus" placeholder="处理状态" clearable @change="loadFeedbacks" style="width: 140px; margin-right: 8px">
                  <el-option label="待处理" value="pending" />
                  <el-option label="处理中" value="processing" />
                  <el-option label="已解决" value="resolved" />
                  <el-option label="已忽略" value="ignored" />
                </el-select>
                <el-button type="primary" plain @click="loadFeedbacks">刷新</el-button>
              </div>
            </div>
          </template>

          <el-table v-loading="feedbackLoading" :data="feedbacks" stripe>
            <el-table-column type="expand">
              <template #default="{ row }">
                <div class="feedback-expand-content">
                  <p style="margin-bottom: 8px; font-weight: bold; color: #374151;">反馈描述：</p>
                  <div class="feedback-detail-text">{{ row.content }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="id" label="ID" width="70" />
            <el-table-column label="反馈类型" width="120">
              <template #default="{ row }">
                <el-tag :type="getTypeTagType(row.feedback_type)" effect="plain">
                  {{ getTypeLabel(row.feedback_type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="title" label="标题 / 简述" min-width="180" show-overflow-tooltip />
            <el-table-column label="所属学校" min-width="160">
              <template #default="{ row }">
                <span>{{ row.user?.school || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="反馈账号" min-width="120">
              <template #default="{ row }">
                <span>{{ row.user?.username || '未知用户' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="contact" label="联系方式" min-width="150" show-overflow-tooltip />
            <el-table-column label="状态" width="110">
              <template #default="{ row }">
                <el-tag :type="getStatusTagType(row.status)" effect="dark">
                  {{ getStatusLabel(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="提交时间" min-width="180">
              <template #default="{ row }">
                <span>{{ formatDateTime(row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right">
              <template #default="{ row }">
                <el-dropdown trigger="click" @command="(status) => handleStatusChange(row, status)" style="margin-right: 12px">
                  <el-button type="primary" size="small" link>
                    更改状态<el-icon class="el-icon--right"><ArrowDown /></el-icon>
                  </el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item command="pending" :disabled="row.status === 'pending'">待处理</el-dropdown-item>
                      <el-dropdown-item command="processing" :disabled="row.status === 'processing'">处理中</el-dropdown-item>
                      <el-dropdown-item command="resolved" :disabled="row.status === 'resolved'">已解决</el-dropdown-item>
                      <el-dropdown-item command="ignored" :disabled="row.status === 'ignored'">已忽略</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                <el-button type="danger" size="small" link @click="handleDeleteFeedback(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="使用进度" name="stats">
        <el-card>
          <template #header>
            <div class="card-header">
              <span style="font-size: 16px; font-weight: bold;">用户使用进度统计</span>
              <el-button type="primary" plain @click="loadUsersStats">刷新</el-button>
            </div>
          </template>

          <el-table v-loading="statsLoading" :data="usersStats" stripe>
            <el-table-column type="expand">
              <template #default="{ row }">
                <div style="padding: 16px 24px; background-color: #f9fafb; border-radius: 8px;">
                  <h4 style="margin-top: 0; margin-bottom: 12px; color: #374151;">各场考试详情</h4>
                  <el-table :data="row.exams" size="small" border style="width: 100%">
                    <el-table-column prop="name" label="考试名称" min-width="250" />
                    <el-table-column prop="schedule_count" label="排考次数" width="150">
                      <template #default="scope">
                        <el-tag size="small" :type="scope.row.schedule_count > 0 ? 'success' : 'info'">
                          {{ scope.row.schedule_count }} 次
                        </el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column prop="download_count" label="下载监考表次数" width="180">
                      <template #default="scope">
                        <el-tag size="small" :type="scope.row.download_count > 0 ? 'warning' : 'info'">
                          {{ scope.row.download_count }} 次
                        </el-tag>
                      </template>
                    </el-table-column>
                  </el-table>
                  <div v-if="!row.exams || row.exams.length === 0" style="color: #909399; font-size: 14px; text-align: center; padding: 10px 0;">
                    暂无考试数据
                  </div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="username" label="用户名" min-width="120" />
            <el-table-column prop="school" label="所属学校" min-width="150" />
            <el-table-column prop="semesters_count" label="创建学期数" width="110" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ row.semesters_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="teachers_count" label="教师人数" width="100" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ row.teachers_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="rooms_count" label="考场数" width="90" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ row.rooms_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="exams_count" label="考试数" width="90" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="success">{{ row.exams_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="total_schedule_count" label="排考总次数" width="110" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="warning" effect="dark">{{ row.total_schedule_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="total_download_count" label="下载总次数" width="100" align="center">
              <template #default="{ row }">
                <el-tag size="small" type="danger" effect="dark">{{ row.total_download_count }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="成绩分析进度" width="120" align="center">
              <template #default="{ row }">
                <el-button type="primary" size="small" plain @click="showGradeProgress(row)">
                  分析进度
                </el-button>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" min-width="160">
              <template #default="{ row }">
                <span>{{ formatDateTime(row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="最后在线时间" min-width="160">
              <template #default="{ row }">
                <el-tag v-if="!row.last_active_at" type="info" size="small">从未在线</el-tag>
                <span v-else>{{ formatDateTime(row.last_active_at) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="dialogVisible" :title="dialogType === 'create' ? '新增用户' : '编辑用户'" width="520px">
      <el-form :model="userForm" label-width="100px">
        <el-form-item label="用户名">
          <el-input v-model="userForm.username" placeholder="至少 3 位" :disabled="dialogType === 'edit'" />
        </el-form-item>
        <el-form-item label="密码">
          <el-input v-model="userForm.password" type="password" show-password :placeholder="dialogType === 'create' ? '至少 6 位' : '不修改请留空，至少 6 位'" />
        </el-form-item>
        <el-form-item label="所属学校">
          <el-input v-model="userForm.school" placeholder="请输入学校全称" />
        </el-form-item>
        <el-form-item label="管理员">
          <el-switch v-model="userForm.is_admin" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="userForm.is_active" />
        </el-form-item>
        <el-form-item label="VIP特权">
          <el-switch v-model="userForm.is_vip" />
        </el-form-item>
        <el-form-item label="VIP到期时间" v-if="userForm.is_vip">
          <el-date-picker
            v-model="userForm.vip_expired_at"
            type="datetime"
            placeholder="留空为永久VIP"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DDTHH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>

    <!-- 成绩分析使用进度详情弹窗 -->
    <el-dialog
      v-model="gradeProgressVisible"
      :title="`【${selectedUserForProgress?.school || selectedUserForProgress?.username || '用户'}】成绩分析使用进度`"
      width="1200px"
      top="6vh"
      destroy-on-close
    >
      <GradeProgress
        v-if="gradeProgressVisible"
        :admin-mode="true"
        :target-user-id="selectedUserForProgress?.id"
      />
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { authApi, feedbackApi } from '@/api'
import { getUser, startImpersonation } from '@/utils/auth'
import GradeProgress from './GradeProgress.vue'

const gradeProgressVisible = ref(false)
const selectedUserForProgress = ref(null)

const showGradeProgress = (row) => {
  selectedUserForProgress.value = row
  gradeProgressVisible.value = true
}

const loading = ref(false)
const submitLoading = ref(false)
const dialogVisible = ref(false)
const dialogType = ref('create') // 'create' or 'edit'
const currentUserId = ref(null)
const users = ref([])
const currentUser = ref(getUser())
const vipEnabled = ref(false)
const vipToggleLoading = ref(false)

// ======================== Feedback Administration ========================
const activeTab = ref('accounts')
const feedbackLoading = ref(false)
const feedbacks = ref([])
const filterType = ref('')
const filterStatus = ref('')

const getTypeLabel = (type) => {
  const map = {
    bug: '🐛 功能问题',
    feature: '✨ 需求想法',
    suggestion: '💡 意见建议',
    other: '❓ 其它'
  }
  return map[type] || type
}

const getTypeTagType = (type) => {
  const map = {
    bug: 'danger',
    feature: 'success',
    suggestion: 'warning',
    other: 'info'
  }
  return map[type] || 'info'
}

const getStatusLabel = (status) => {
  const map = {
    pending: '待处理',
    processing: '处理中',
    resolved: '已解决',
    ignored: '已忽略'
  }
  return map[status] || status
}

const getStatusTagType = (status) => {
  const map = {
    pending: 'warning',
    processing: 'primary',
    resolved: 'success',
    ignored: 'info'
  }
  return map[status] || 'info'
}

const formatDateTime = (val) => {
  if (!val) return ''
  let dateStr = val
  if (typeof val === 'string' && !val.endsWith('Z') && !val.includes('+')) {
    dateStr = val + 'Z'
  }
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return val
  
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const loadFeedbacks = async () => {
  feedbackLoading.value = true
  try {
    feedbacks.value = await feedbackApi.list({
      feedback_type: filterType.value || null,
      status_filter: filterStatus.value || null
    })
  } catch (e) {
    console.error(e)
  } finally {
    feedbackLoading.value = false
  }
}

const handleStatusChange = async (row, status) => {
  try {
    await feedbackApi.updateStatus(row.id, { status })
    ElMessage.success('反馈状态更新成功')
    await loadFeedbacks()
  } catch (e) {
    console.error(e)
  }
}

const handleDeleteFeedback = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除此条反馈记录吗？此操作不可撤销。', '确认删除', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await feedbackApi.delete(row.id)
    ElMessage.success('反馈记录删除成功')
    await loadFeedbacks()
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
    }
  }
}

const statsLoading = ref(false)
const usersStats = ref([])

const loadUsersStats = async () => {
  statsLoading.value = true
  try {
    usersStats.value = await authApi.getUsersStats()
  } catch (e) {
    console.error(e)
    ElMessage.error('加载使用进度失败')
  } finally {
    statsLoading.value = false
  }
}

watch(activeTab, (val) => {
  if (val === 'feedback') {
    loadFeedbacks()
  } else if (val === 'stats') {
    loadUsersStats()
  }
})

const userForm = reactive({
  username: '',
  password: '',
  school: '',
  is_admin: false,
  is_active: true,
  is_vip: false,
  vip_expired_at: ''
})

const isRowVip = (row) => {
  if (!row.is_vip) return false
  if (!row.vip_expired_at) return true
  const expiredAt = new Date(row.vip_expired_at)
  return expiredAt.getTime() > Date.now()
}

const formatVipExpiredAt = (val) => {
  return formatDateTime(val)
}

const loadUsers = async () => {
  loading.value = true
  try {
    users.value = await authApi.getUsers()
  } finally {
    loading.value = false
  }
}

const loadSettings = async () => {
  try {
    const res = await authApi.getSettings()
    vipEnabled.value = res.vip_enabled
  } catch (e) {
    console.error(e)
  }
}

const handleVipToggle = async (val) => {
  vipToggleLoading.value = true
  try {
    await authApi.updateSettings({ vip_enabled: val })
    ElMessage.success(`VIP限制已${val ? '开启' : '关闭（全员免费可用）'}`)
  } catch (e) {
    console.error(e)
    vipEnabled.value = !val
  } finally {
    vipToggleLoading.value = false
  }
}

const openCreateDialog = () => {
  dialogType.value = 'create'
  currentUserId.value = null
  userForm.username = ''
  userForm.password = ''
  userForm.school = ''
  userForm.is_admin = false
  userForm.is_active = true
  userForm.is_vip = false
  userForm.vip_expired_at = ''
  dialogVisible.value = true
}

const openEditDialog = (row) => {
  dialogType.value = 'edit'
  currentUserId.value = row.id
  userForm.username = row.username
  userForm.password = ''
  userForm.school = row.school || ''
  userForm.is_admin = row.is_admin
  userForm.is_active = row.is_active
  userForm.is_vip = row.is_vip
  userForm.vip_expired_at = row.vip_expired_at || ''
  dialogVisible.value = true
}

const submitForm = async () => {
  if (dialogType.value === 'create') {
    if (!userForm.username || userForm.username.length < 3) {
      ElMessage.warning('用户名至少 3 位')
      return
    }
    if (!userForm.password || userForm.password.length < 6) {
      ElMessage.warning('密码至少 6 位')
      return
    }
  } else {
    if (userForm.password && userForm.password.length < 6) {
      ElMessage.warning('密码至少 6 位')
      return
    }
  }

  submitLoading.value = true
  try {
    const data = {
      school: userForm.school,
      is_admin: userForm.is_admin,
      is_active: userForm.is_active,
      is_vip: userForm.is_vip,
      vip_expired_at: userForm.is_vip && userForm.vip_expired_at ? userForm.vip_expired_at : null
    }
    if (userForm.password) {
      data.password = userForm.password
    }

    if (dialogType.value === 'create') {
      data.username = userForm.username
      await authApi.createUser(data)
      ElMessage.success('用户创建成功')
    } else {
      await authApi.updateUser(currentUserId.value, data)
      ElMessage.success('用户修改成功')
    }
    dialogVisible.value = false
    await loadUsers()
  } finally {
    submitLoading.value = false
  }
}

const handleSuperLogin = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定以用户【${row.username}】的身份一键登录吗？此操作不会挤掉该用户的当前登录。`,
      '超级登录确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    )
    const res = await authApi.superLogin(row.id)
    if (res && res.access_token) {
      startImpersonation(res.access_token, res.user)
      ElMessage.success(`超级登录成功，当前已切换为【${row.username}】的身份`)
      window.location.href = '/'
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error(error)
      ElMessage.error(error.response?.data?.detail || '超级登录失败')
    }
  }
}

onMounted(() => {
  loadUsers()
  loadSettings()
})
</script>

<style scoped>
.user-management {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.feedback-filters {
  display: flex;
  align-items: center;
}

.feedback-expand-content {
  padding: 16px 24px;
  background-color: #f9fafb;
  border-radius: 8px;
}

.feedback-detail-text {
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  white-space: pre-wrap;
  word-break: break-all;
}

.admin-tabs {
  background: #ffffff;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Blend el-card seamlessly into tabs */
.admin-tabs :deep(.el-card) {
  border: none;
  box-shadow: none;
}

.admin-tabs :deep(.el-card__header) {
  padding-left: 0;
  padding-right: 0;
}

.admin-tabs :deep(.el-card__body) {
  padding-left: 0;
  padding-right: 0;
}
</style>
